export const siteConfig = {
  siteName: "Fownd",
  siteUrl: "https://fownd.care",
  defaultTitle: "Real-Time AI Documentation for Physical Therapy",
  defaultDescription: "FOWND delivers live, compliant SOAP notes during sessions—built for PT workflows, reducing admin time and improving care.",
  defaultKeywords: "AI SOAP notes, physical therapy documentation, AI clinical notes, automated SOAP notes, PT documentation, clinical AI, Fownd, Notation",
  twitterHandle: "@fowndcare",
  logoUrl: "/fownd.png"
};

export const generateTitle = (pageTitle?: string, includeSiteName = true): string => {
  if (!pageTitle) return siteConfig.defaultTitle;
  return includeSiteName ? `${pageTitle} | ${siteConfig.siteName}` : pageTitle;
};

export const generateDescription = (pageDescription?: string): string => {
  return pageDescription || siteConfig.defaultDescription;
};

export const getCanonicalUrl = (path?: string): string => {
  const cleanPath = path?.startsWith('/') ? path : `/${path || ''}`;
  return `${siteConfig.siteUrl}${cleanPath}`;
};

export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": getCanonicalUrl(crumb.url)
    }))
  };
};

export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Fownd",
    "url": siteConfig.siteUrl,
    "logo": getCanonicalUrl(siteConfig.logoUrl),
    "description": siteConfig.defaultDescription,
    "foundingDate": "2023",
    "industry": "Healthcare Technology",
    "speciality": "AI-Powered Clinical Documentation",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "sameAs": [
      "https://linkedin.com/company/fownd"
    ]
  };
};

export const generateSoftwareApplicationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Fownd AI SOAP Notes",
    "description": "AI-powered clinical documentation tool for physical therapists",
    "url": siteConfig.siteUrl,
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "creator": {
      "@type": "Organization",
      "name": "Fownd"
    },
    "softwareVersion": "1.0",
    "releaseNotes": "AI-assisted SOAP note generation for physical therapy practices"
  };
};

export const generateBlogPostSchema = (article: {
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  url: string;
  image?: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image ? getCanonicalUrl(article.image) : getCanonicalUrl(siteConfig.logoUrl),
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Fownd",
      "logo": {
        "@type": "ImageObject",
        "url": getCanonicalUrl(siteConfig.logoUrl)
      }
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": getCanonicalUrl(article.url)
    }
  };
};

export const generateVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  embedUrl?: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.name,
    "description": video.description,
    "thumbnailUrl": getCanonicalUrl(video.thumbnailUrl),
    "uploadDate": video.uploadDate,
    "duration": video.duration,
    "embedUrl": video.embedUrl,
    "publisher": {
      "@type": "Organization",
      "name": "Fownd",
      "logo": {
        "@type": "ImageObject",
        "url": getCanonicalUrl(siteConfig.logoUrl)
      }
    }
  };
};

// Page-specific SEO configurations
export const pageConfigs = {
  home: {
    title: "Real-Time AI Documentation for Physical Therapy",
    description: "FOWND delivers live, compliant SOAP notes during sessions—built for PT workflows, reducing admin time and improving care.",
    keywords: siteConfig.defaultKeywords,
    canonical: "/",
    schema: [generateOrganizationSchema(), generateSoftwareApplicationSchema()]
  },
  about: {
    title: "Fownd Team | Innovators in AI for Physical Therapy",
    description: "Meet Fownd's innovators—PTs, engineers, and designers uniting to transform healthcare with AI-powered solutions.",
    keywords: "Fownd team, AI healthcare innovation, physical therapy technology, clinical documentation automation",
    canonical: "/about"
  },
  roiCalculator: {
    title: "ROI Calculator - automated SOAP notes for outpatient PT",
    description: "Calculate your savings when SOAP notes write themselves—while you treat, not after.",
    keywords: "ROI calculator, physical therapy savings, AI documentation ROI, practice efficiency calculator",
    canonical: "/roi-calculator",
    schema: [generateSoftwareApplicationSchema()]
  },
  contact: {
    title: "Contact Us - AI SOAP Notes for Physical Therapists",
    description: "FOWND's AI SOAP Notes for Physical Therapists, designed specifically for demanding clinics, improving patient session quickness, preciseness, and compliance.",
    keywords: "contact Fownd, AI SOAP notes demo, physical therapy software inquiry",
    canonical: "/contact"
  },
  privacyPolicy: {
    title: "Privacy Policy - AI documentation physical therapy",
    description: "Learn how FOWND safeguards your data with HIPAA-compliant, real-time documentation for PT clinics.",
    keywords: "privacy policy, HIPAA compliance, data protection, healthcare privacy",
    canonical: "/privacy-policy",
    noindex: true
  },
  termsConditions: {
    title: "Terms – PT Documentation AI",
    description: "HIPAA Compliant",
    keywords: "terms and conditions, service agreement, healthcare software terms",
    canonical: "/terms-conditions",
    noindex: true
  },
  demo: {
    title: "Demo – See Live PT Documentation in Action",
    description: "Watch FOWND create accurate SOAP notes during the session—no transcription delay, no extra clicks.",
    keywords: "AI SOAP notes demo, clinical documentation demo, physical therapy AI demo",
    canonical: "/demo"
  },
  detailedRoiCalculator: {
    title: "Clinic Financial Impact Calculator for PT | Fownd Notation",
    description: "Calculate your PT clinic's financial impact from audits, denials, and documentation with Fownd's AI-powered Notation tool.",
    keywords: "detailed ROI calculator, advanced practice analytics, PT savings analysis, implementation ROI",
    canonical: "/detailed-roi-calculator",
    schema: [generateSoftwareApplicationSchema()]
  },
  blogs: {
    title: "PT AI Documentation Blog | SOAP Notes & Clinical Efficiency",
    description: "Expert insights on AI for SOAP notes, clinical documentation, and PT workflows. Learn about AI documentation tools, outcome tracking, and healthcare technology trends in physical therapy.",
    keywords: "AI SOAP notes, physical therapy blog, clinical documentation AI, healthcare technology insights, PT documentation tools, AI clinical notes",
    canonical: "/blogs"
  },
  businessModel: {
    title: "Boost PT Efficiency with Clinical Documentation AI",
    description: "Experience PT-specific AI that drafts notes as you treat—faster workflows, zero errors, and more patient time.",
    keywords: "Fownd business model, healthcare AI business, sustainable AI healthcare",
    canonical: "/business-model"
  },
  founderNotes: {
    title: "AI Advancements in PT",
    description: "Founder Insights",
    keywords: "founder notes, AI healthcare vision, startup insights, healthcare innovation",
    canonical: "/founder-notes"
  },
  cookiePolicy: {
    title: "Cookie Policy - PT documentation software | AI EMR - Fownd",
    description: "Learn how Fownd uses cookies to improve site performance, secure data, and enhance your AI EMR experience for physical therapy clinics.",
    keywords: "cookie policy, website cookies, privacy settings",
    canonical: "/cookie-policy",
    noindex: true
  },
  faq: {
    title: "FAQ - clinical documentation AI | HIPAA-compliant AI EMR",
    description: "With a HIPAA-compliant AI EMR, FOWND provides clinical documentation AI that speeds up note-taking, decrease errors, and prevent damage to patient data.",
    keywords: "FOWND FAQ, Notation FAQ, AI documentation questions, physical therapy AI, clinical assistant FAQ, SOAP notes AI",
    canonical: "/faq"
  },
  // Product page
  notation: {
    title: "Notation by Fownd | AI Clinical Documentation for PTs",
    description: "Real-time AI EMR for physical therapy. Notation auto-generates HIPAA-compliant SOAP notes, cutting documentation time and improving patient care.",
    keywords: "Notation AI, AI EMR, real-time documentation, HIPAA compliant SOAP notes, PT documentation AI",
    canonical: "/notation"
  },
  // Blog post configurations
  blogDocumentationPT: {
    title: "The Power of Documentation in Physical Therapy | Fownd",
    description: "Learn how effective documentation boosts patient outcomes, clinic efficiency, compliance, and engagement in physical therapy practice.",
    keywords: "physical therapy documentation, PT notes importance, clinical documentation best practices",
    canonical: "/blogs/documentation-physical-therapy"
  },
  blogAdvocacyReimbursement: {
    title: "Fownd's Vision For Advocacy and Higher Reimbursement - Fownd Blog",
    description: "Discover how PT clinics can use data, patient outcomes, and policy advocacy to secure higher reimbursement and strengthen their value.",
    keywords: "PT reimbursement, healthcare advocacy, AI documentation benefits, practice revenue",
    canonical: "/blogs/advocacy-reimbursement"
  },
  blogAIHealthcareBias: {
    title: "AI in Healthcare: Tackling Bias for Better Care - Fownd Blog",
    description: "Discover how Fownd builds fair AI tools to reduce bias in healthcare, support clinicians, and deliver equitable, high-quality patient care.",
    keywords: "AI healthcare bias, equitable healthcare AI, bias in medical AI, healthcare equity",
    canonical: "/blogs/ai-healthcare-bias"
  },
  // Blog Posts
  blogProvingOutcomes: {
    title: "Proving Outcomes Is the Future of PT — Are We Ready? | Fownd Blog",
    description: "Learn how outcome tracking in PT boosts value-based care, improves reimbursement, and showcases measurable impact for patients and payers.",
    keywords: "PT outcomes, value-based care, physical therapy metrics, outcome tracking, healthcare outcomes, PT data, clinical outcomes, physical therapy future",
    canonical: "/blogs/proving-outcomes-future-pt",
    ogImage: "/blog/blog-5.png"
  },
  blogSessionWroteNote: {
    title: "What If the Session Wrote the Note? | Fownd Blog",
    description: "Discover how real-time documentation can free PTs from end-of-day note burden, capture accurate details, and keep focus on patient care.",
    keywords: "PT documentation, real-time documentation, clinical workflow, SOAP notes, physical therapy efficiency, clinical documentation",
    canonical: "/blogs/session-wrote-note",
    ogImage: "/blog/blog-4.png"
  },
  blogWhyNotPTFirst: {
    title: "Why Not PT First? Rethinking MSK care at the front door | Fownd Blog",
    description: "Exploring the growing conversation about physical therapists serving as first-contact providers for musculoskeletal care and what needs to change to get there.",
    keywords: "PT first contact, direct access physical therapy, MSK care, physical therapy autonomy, healthcare policy, value-based care, clinical reasoning",
    canonical: "/blogs/why-not-pt-first",
    ogImage: "/blog/blog-6.png"
  },
  careers: {
    title: "Careers at Fownd | Join Our Healthcare AI Team",
    description: "Join Fownd's mission to transform healthcare with AI. We're building the future of clinical documentation for physical therapists. View open positions.",
    keywords: "Fownd careers, healthcare AI jobs, clinical documentation careers, PT technology jobs, AI healthcare startup jobs, medical AI careers",
    canonical: "/careers"
  },
  blogAISoapNotes: {
    title: "AI for SOAP Notes: Everything a PT Should Know in 2025 | Fownd Blog",
    description: "Complete guide to AI documentation tools for physical therapists. Learn about benefits, risks, and what to look for in AI SOAP note solutions.",
    keywords: "AI SOAP notes, AI documentation, physical therapy AI, clinical documentation, AI medical notes, PT documentation tools, healthcare AI",
    canonical: "/blogs/ai-soap-notes-pt-guide-2025",
    ogImage: "/blog/default.jpg"
  },
  blogTop5AITools: {
    title: "Top 5 AI Note-Taking Tools for PT Clinics (and Where Fownd Fits In) | Fownd Blog",
    description: "Compare the leading AI documentation tools for physical therapy clinics. Discover how Fownd Notation stands out as the only AI-native solution built by PTs, for PTs.",
    keywords: "AI note-taking tools, PT clinic software, physical therapy AI, Nuance DAX, Suki AI, WebPT Scribe, Prompt EMR, Fownd Notation, AI documentation comparison",
    canonical: "/blogs/top-5-ai-note-taking-tools-pt-clinics",
    ogImage: "/blog/default.jpg"
  },
  blogAIScribesHealthcare: {
    title: "The Rise of AI Scribes in Healthcare: Why Notation Is Poised to Lead Physical Therapy Into the Future | Fownd Blog",
    description: "Discover how Notation by Fownd goes beyond generic AI scribes, offering PT-specific intelligence for clinical documentation, workflow support, and compliance.",
    keywords: "AI scribes, healthcare AI, physical therapy documentation, PT-GPT, clinical documentation AI, Notation by Fownd, AI medical scribes, PT workflow automation",
    canonical: "/blogs/ai-scribes-healthcare-notation-physical-therapy-future",
    ogImage: "/blog/default.jpg"
  },
  blogFutureOfDocumentation: {
    title: "AI Documentation for Physical Therapists | Notation",
    description: "Discover how AI-powered documentation can follow your PT sessions naturally instead of forcing care into rigid forms. Built with clinicians for real practice.",
    keywords: "AI documentation, physical therapy documentation, PT documentation AI, clinical workflow, AI SOAP notes, healthcare AI, clinical documentation, PT documentation tools",
    canonical: "/blogs/ai-documentation-follows-visit-not-form",
    ogImage: "/blog/blog-10.png"
  },
  blogAIScribeVsRealtime: {
    title: "AI Scribe vs Real-Time Documentation for Physical Therapists",
    description: "Learn the real difference between AI scribes and real-time documentation tools for PTs—and which approach truly reduces charting time and burnout.",
    keywords: "AI scribe, real-time documentation, PT documentation, AI scribe vs real-time, physical therapy documentation tools, clinical workflow, documentation burnout, Notation",
    canonical: "/blogs/ai-scribe-vs-realtime-documentation",
    ogImage: "/blog/blog-11.png"
  },
  blogWhyNotation: {
    title: "Why Notation? We Are More Than Just an AI Scribe | Fownd",
    description: "Notation gives your EMR superpowers without hefty integration costs. Privacy-first AI, PT-in-the-loop documentation, computer vision, and Home Health workflows built for clinicians.",
    keywords: "Notation, AI scribe, PT documentation, computer vision PT, OASIS documentation, Home Health AI, physical therapy AI, clinical documentation, Fownd",
    canonical: "/blogs/why-notation-more-than-ai-scribe",
    ogImage: "/blog/Booth1.png"
  }
}; 